import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Upload, Receipt, Save, Store } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function fileToDataUrl(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

const FIELDS = [
  { key: "business_name", label: "Business name", placeholder: "e.g. Beltran Medical Clinic / Medbay Medical Supplies" },
  { key: "registered_name", label: "Registered name (smaller line)", placeholder: "e.g. Beltran Medical Clinic & Gonzales Medical Clinic" },
  { key: "registration_number", label: "Registration No.", placeholder: "e.g. 20230155330000379" },
  { key: "email", label: "Email", placeholder: "e.g. medbaypharmacy@gmail.com" },
  { key: "phone_mobile", label: "Phone — Mobile", placeholder: "e.g. +63 967 322 4061" },
  { key: "phone_landline", label: "Phone — Fixed Line", placeholder: "e.g. 075-568 3136" },
  { key: "receipt_title", label: "Document title", placeholder: "e.g. Official Receipt / Purchase Order" },
  { key: "tin", label: "TIN (optional)", placeholder: "e.g. 000-000-000-000" },
  { key: "vat_registration", label: "VAT registration (optional)", placeholder: 'e.g. "VAT Reg" or "NON-VAT"' },
];

const PAPER_OPTIONS = [
  { value: "58mm", label: "Thermal 58 mm" },
  { value: "80mm", label: "Thermal 80 mm" },
  { value: "A4", label: "A4 sheet" },
];

const EMPTY = {
  business_name: "", registered_name: "", registration_number: "", address: "",
  email: "", phone_mobile: "", phone_landline: "", contact_number: "", tin: "",
  vat_registration: "", receipt_title: "", paper_width: "80mm", footer_note: "", logo_url: "",
};

export default function POSReceiptSettings() {
  const { user } = useAuth();
  const isAdmin = ["admin", "hospital_admin"].includes(user?.role);
  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/pos-receipt-header");
      setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])) });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB image"); return; }
    try {
      const url = await fileToDataUrl(f);
      setForm((s) => ({ ...s, logo_url: url }));
    } catch { toast.error("Couldn't read file"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/pos-receipt-header", form);
      toast.success("Receipt header saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="pos-receipt-settings-page">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
        <Receipt size={22} /> POS Receipt Header
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        The business letterhead printed at the top of sales receipts. This is separate from the doctor's
        prescription letterhead — receipts show your store / clinic business details instead.
      </p>

      <Card className="mt-5 p-5 space-y-4">
        {/* Logo */}
        <div className="flex items-start gap-5">
          <div className="w-32 h-32 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center overflow-hidden">
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <Store size={36} className="text-slate-300" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="text-sm font-semibold text-slate-700">Business logo (optional)</div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="receipt-logo-file" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!isAdmin}>
              <Upload size={14} className="mr-1" />Upload logo
            </Button>
            {form.logo_url && isAdmin ? (
              <Button variant="ghost" className="text-red-600 ml-2" onClick={() => setForm((s) => ({ ...s, logo_url: "" }))}>Remove</Button>
            ) : null}
            {!isAdmin && <div className="text-xs text-slate-500">Only admin / hospital admin can change this.</div>}
          </div>
        </div>

        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.label}</label>
            <Input value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} disabled={!isAdmin} data-testid={`receipt-${f.key}`} />
          </div>
        ))}

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Address</label>
          <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!isAdmin}
            placeholder="Erfe Street, Beltran & Jimenez Bldg.&#10;San Fabian, Pangasinan 2433&#10;Philippines"
            className="mt-1 w-full border border-slate-300 rounded-lg p-3 min-h-[70px]" data-testid="receipt-address" />
          <div className="text-[11px] text-slate-400 mt-1">One line per row — line breaks are preserved on the receipt.</div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Receipt paper size</label>
          <div className="flex gap-2 mt-1">
            {PAPER_OPTIONS.map((p) => (
              <button
                key={p.value}
                type="button"
                disabled={!isAdmin}
                onClick={() => setForm({ ...form, paper_width: p.value })}
                className={`px-3 py-2 rounded-lg border text-sm ${form.paper_width === p.value ? "border-blue-600 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 text-slate-600"}`}
                data-testid={`receipt-paper-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Footer note</label>
          <textarea value={form.footer_note} onChange={(e) => setForm({ ...form, footer_note: e.target.value })} disabled={!isAdmin}
            placeholder="e.g. This serves as your Official Receipt. Thank you for your purchase."
            className="mt-1 w-full border border-slate-300 rounded-lg p-3 min-h-[80px]" data-testid="receipt-footer-note" />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !isAdmin} className="bg-blue-700 hover:bg-blue-800" data-testid="receipt-save"><Save size={14} className="mr-1" />Save</Button>
        </div>
      </Card>
    </div>
  );
}
